import React, { useEffect, useState, useMemo, ChangeEvent, useCallback } from "react";
import { PersonalCreditInfoForm } from "src/app/views";
import { useLocationLibbyCall, useLocationDAO } from "src/app/business";
import { State, City } from "src/app/models";
import { useDebounce } from "src/commons";
import { orderBy } from "lodash";
import { PROVINCES } from "src/app/const";
import { PersonalInfoInitialValue, ApplicantInitialValue, PaymentInitialValue, applicantInitialValue } from "../../initialValues";
import { ValuesForm, FormProps } from "../../types";
import { useCheckoutFormContext, useCreditAddressLogic } from "../../hooks";

interface Values extends ValuesForm {
  personalInfo: PersonalInfoInitialValue;
  applicantInfo: ApplicantInitialValue<Partial<File>>;
  paymentMethod: PaymentInitialValue;
}

export const ApplicantInfoForm = ({ onBackStep }: FormProps) => {
  const { form, values, errors, handleSubmit } = useCheckoutFormContext();
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const locationDAO = useLocationDAO();
  const { data, working } = useLocationLibbyCall<State[]>({ methodName: "getStates" });
  useCreditAddressLogic("applicantInfo");

  const valuesAll = useMemo(
    () =>
      ({
        applicantInfo: applicantInitialValue,
        ...values,
      } as Values),
    [values],
  );

  const provinces = useMemo(() => {
    let provincesFiltered: State[] = data?.filter((item) => item.id.toString() === PROVINCES.CATAMARCA.toString()) || [];
    if (valuesAll.useDeliveryAddress) {
      provincesFiltered = [...provincesFiltered, valuesAll.deliveryInfo.province];
    }
    return provincesFiltered;
  }, [data, valuesAll.useDeliveryAddress, valuesAll.deliveryInfo.province]);

  const [search, setSearch] = useState(valuesAll.applicantInfo.city?.name || "");
  const searchDebunce = useDebounce(search, 600);

  useEffect(() => {
    if (valuesAll.useDeliveryAddress && search !== valuesAll.deliveryInfo.city.name) {
      setSearch(valuesAll.deliveryInfo.city.name);
    } else if (!valuesAll.useDeliveryAddress && search === valuesAll.deliveryInfo.city.name) {
      setSearch("");
    }
  }, [valuesAll.useDeliveryAddress, valuesAll.deliveryInfo.city.name, search]);

  const selectedProvince = useMemo(() => valuesAll.applicantInfo.province?.id !== "", [valuesAll.applicantInfo.province?.id]);

  const zitCode = useMemo(() => valuesAll.applicantInfo.zipCode || "", [valuesAll.applicantInfo.zipCode]);

  const onInputChangeCity = useCallback(
    (event: ChangeEvent<HTMLInputElement>, value: string) => {
      setSearch(value);
    },
    [setSearch],
  );

  useEffect(() => {
    if (
      (valuesAll?.applicantInfo?.city?.name?.length > 0 && valuesAll?.applicantInfo?.city?.name !== search) ||
      valuesAll?.applicantInfo?.city === null
    ) {
      const copyValuesForm = { ...valuesAll };
      copyValuesForm.applicantInfo.city = applicantInitialValue.city;
      form.reset(copyValuesForm);
    }
  }, [search, form, valuesAll]);

  useEffect(() => {
    if (selectedProvince && searchDebunce && searchDebunce !== valuesAll.applicantInfo.city?.name) {
      setLoading(true);
      (async () => {
        const city = await locationDAO.getCityByState(valuesAll.applicantInfo.province?.id, searchDebunce);
        setCities(city);
        setLoading(false);
      })();
    }
  }, [selectedProvince, locationDAO, valuesAll.applicantInfo.province, searchDebunce, valuesAll.applicantInfo.city]);

  const invalidZipCode = useMemo(
    () => !!zitCode && !valuesAll.applicantInfo.city?.zip?.includes(Number(zitCode)),
    [valuesAll.applicantInfo.city, zitCode],
  );

  return (
    <PersonalCreditInfoForm
      title="Datos del solicitante"
      nameForm="applicantInfo"
      showCheckbox
      nameDelivery="applicantInfo"
      loading={working}
      invalidZidCode={invalidZipCode}
      loadingCities={loading}
      disableNext={Object.keys(errors as any).length !== 0}
      onClickStep={handleSubmit}
      onBackStep={onBackStep}
      form={form}
      provinces={orderBy(provinces, "name")}
      selectedProvince={selectedProvince}
      cities={cities}
      onInputChangeCity={onInputChangeCity}
      zipCodeLength={zitCode.length || 0}
      searchCityValue={search}
      useSameDeliveryAddress={valuesAll.useDeliveryAddress}
    />
  );
};
